import { createApi } from "@reduxjs/toolkit/query/react";
import { baseQueryWithReauth } from "@src/utils/baseQuery";
import { APIResponse } from "./CommonApiSlice";

export interface CreateBranchPayload {
  name: string;
  parentReference: string;
  organizationType: string;
  description?: string;
  state: string;
  country: string;
  address: string;
  email: string;
  phoneNumber: string;
}

export interface BranchesDropdown {
  parentId: string;
  organizationType?: string;
}

export interface GetOrgDropdownResponse extends APIResponse {
  response: { name: string; id: string; syllabusAssigned: boolean }[];
}

export interface Branch {
  createdOn: string;
  lastUpdateOn: string;
  createdByName: string;
  createdByEmail: string;
  modifiedByName: string;
  modifiedByEmail: string;
  id: string;
  name: string;
  parentReference: string;
  organizationType: string; // Adjust if needed
  country: string;
  state: string;
  district: string;
  city: string;
  address: string;
  email: string;
  phoneNumber: string;
}

interface GetBranchesDropdownResponse extends APIResponse {
  response: Branch[];
}

interface GetAllBranchesForOrgApiResponse extends APIResponse {
  response: {
    totalPages: number;
    totalElements: number;
    size: number;
    content: Branch[];
    last?: boolean;
  };
}

type CreateBranchApiResponse = APIResponse;

export interface UpdateBranchPayload {
  name: string;
  parentReference?: string;
  organizationType?: string;
  country: string;
  state: string;
  pinCode: string;
  address: string;
  email: string;
  phoneNumber: string;
}

export interface UpdateBranchResponse extends APIResponse {
  response: Branch;
}

export const BranchApiSlice = createApi({
  reducerPath: "branchApi",
  baseQuery: baseQueryWithReauth,
  endpoints: builder => ({
    getAllBranchesforOrg: builder.query<
      GetAllBranchesForOrgApiResponse,
      { organizationId: string; pageNo: number; pageSize: number }
    >({
      query: ({ organizationId, pageNo, pageSize }) => ({
        url: `/branch/get-all-branches?organizationId=${organizationId}&pageNo=${pageNo}&pageSize=${pageSize}`,
        method: "GET",
      }),
    }),
    getOrgDropdown: builder.query<
      GetOrgDropdownResponse,
      { type: string; parentId?: string }
    >({
      query: ({ type, parentId }) => ({
        url: `/organization/get-organization-dropdown?type=${type}&parentId=${parentId}`,
        method: "GET",
      }),
    }),
    createBranch: builder.mutation<
      CreateBranchApiResponse,
      CreateBranchPayload
    >({
      query: payload => ({
        url: "branch",
        method: "POST",
        body: payload,
      }),
    }),
    updateBranch: builder.mutation<
      UpdateBranchResponse,
      { branchId: string; editorEmail: string; payload: UpdateBranchPayload }
    >({
      query: ({ branchId, editorEmail, payload }) => ({
        url: `branch/${branchId}?editorEmail=${encodeURIComponent(editorEmail)}`,
        method: "PUT",
        body: payload,
      }),
    }),
    getBranchesDropdown: builder.mutation<
      GetBranchesDropdownResponse,
      BranchesDropdown[]
    >({
      query: payload => ({
        url: "branch/get-branches-parents",
        method: "POST",
        body: payload,
      }),
    }),
  }),
});
export const {
  useGetAllBranchesforOrgQuery,
  useLazyGetAllBranchesforOrgQuery,
  useGetOrgDropdownQuery,
  useCreateBranchMutation,
  useUpdateBranchMutation,
  useLazyGetOrgDropdownQuery,
  useGetBranchesDropdownMutation,
} = BranchApiSlice;
